import { defineStore } from 'pinia'
import { SelectedPCPOption } from '~/components/Product/ProfessionalCarePlan.vue'

export type ProductStoreLocation = 'quickview' | 'pdp'
export const useProductStore = (productLocationState: ProductStoreLocation) => {
  return defineStore(productLocationState, () => {
    const { locale } = useNuxtApp().$i18n
    // Keep the store reactive when preferences initialize after this store.
    const preferencesStore = usePreferencesStore()
    // SAFETY: every locale in localeConfig carries one of the three SiteIdKey values.
    const siteId = localeConfig[locale.value]?.siteId as SiteIdKey

    // state
    const { getProducts } = useProducts()
    const { trackItemDetailLoad } = useConstructorTracker()
    const { rememberedRingSize, remember: rememberRingSize } = useRememberedRingSize()
    const selectedProduct = ref<FlattenedProduct | null | undefined>(null)
    const selectedBasketItemId = ref<string | null>(null)
    const selectedRingSize = ref<string | undefined>(undefined)
    const selectedPcpOption = ref<SelectedPCPOption | null>()
    const engravingText = ref<string | undefined>(undefined)
    const ringSizeError = ref<string>()
    const ringSizeErrorAttempt = ref(0)
    const masterProduct = ref<FlattenedProduct | null>(null)
    const isFetching = ref(false)

    // getters
    const isProductNotFound = computed(() => !selectedProduct.value)
    const isEngraved = computed(() => !!engravingText.value?.trim())

    const isMutliSizeProductWithoutSelectedSize = computed(() => {
      return (
        !!selectedProduct.value?.c_mhj_processedSizeOptions &&
        selectedProduct.value?.c_mhj_processedSizeOptions.length > 1 &&
        !selectedProcessedSizeOptions.value
      )
    })

    const productAvailabilityModel = computed(() => {
      const stockLevels: ProductWithInventory = {
        inventory: selectedProduct.value?.inventory,
        isReplenished: selectedProduct.value?.c_replenished,
      }
      return getProductAvailabilityModel(stockLevels, PRODUCT_STOCK_LOW_THRESHOLD[siteId])
    })

    const selectedProcessedSizeOptions = computed<processedSizeOptions | undefined>(() => {
      if (!selectedProduct.value || !selectedRingSize.value) return
      return selectedProduct.value?.c_mhj_processedSizeOptions?.find(
        (specialOrderSku) => specialOrderSku.size === selectedRingSize.value,
      )
    })

    const selectedProductConstructorId = computed(() => mapConstructorItemId(selectedProduct.value))

    const selectedProductUSPs = computed(() => {
      let pdpReferenceList = []

      pdpReferenceList =
        preferencesStore.pdp?.productUspAttribute?.filter(
          (usp: ProductUSP) =>
            usp.fields?.referenceIdentifier &&
            selectedProduct.value?.[usp.fields?.referenceIdentifier] &&
            (!usp.fields?.categoryId || usp.fields?.categoryId === selectedProduct.value?.primaryCategoryId),
        ) || []

      const gold = preferencesStore.pdp?.productUspAttribute?.find(
        (usp: ProductUSP) => usp.fields?.referenceIdentifier === 'custom_10ctGuarantee',
      )

      if (gold && selectedProduct.value?.c_metalContent && selectedProduct.value?.c_metalContent?.startsWith('10 K')) {
        pdpReferenceList.push(gold)
      }

      const watchType = preferencesStore.pdp?.productUspAttribute?.find(
        (usp: ProductUSP) => usp.fields?.referenceIdentifier === 'watchType',
      )

      if (selectedProduct.value?.c_watchType && watchType) {
        pdpReferenceList.push(watchType)
      }

      return pdpReferenceList ?? []
    })

    // actions
    function setSelectedRingSize(size: string) {
      selectedRingSize.value = size
    }

    // Also clears a size carried over from the previous product.
    function applyDefaultRingSize() {
      selectedRingSize.value = getDefaultRingSize(selectedProduct.value, rememberedRingSize.value)
    }

    function rememberSelectedRingSize() {
      // Bag edits start from the basket item's size, so they don't count as a new shopper choice.
      if (selectedRingSize.value && !selectedBasketItemId.value) rememberRingSize(selectedRingSize.value)
    }

    function setSelectedBasketItem(itemId: string) {
      selectedBasketItemId.value = itemId
    }

    function setPcpOption(option: SelectedPCPOption) {
      selectedPcpOption.value = option
    }

    function setEngravingText(value: string) {
      engravingText.value = value
    }

    function setProduct(product: FlattenedProduct) {
      selectedProduct.value = product
    }

    function setRingSizeError(errorText: string) {
      ringSizeError.value = errorText
      if (errorText) ringSizeErrorAttempt.value++
    }

    async function getNewVariant(sku: string) {
      const foundProducts = await getProducts([sku])
      if (!foundProducts || foundProducts instanceof Error) return (selectedProduct.value = null)
      selectedProduct.value = foundProducts[0]

      // client-side only tracking events - PDP tracking is now handled in [product].vue
      if (import.meta.client && productLocationState !== 'pdp' && !selectedProduct.value?.type?.variationGroup) {
        if (!selectedProduct.value) {
          return
        }
        trackProductView(selectedProduct.value, 'quickview', 'quickview', locale.value)
        // Braze integration - Product View event
        useBrazeTracking().logCustomEvent('product_view', {
          product_id: selectedProduct.value.id,
          product_name: selectedProduct.value.name,
          product_price: selectedProduct.value.price,
          product_image_url: findPrimaryAngle(selectedProduct.value.images),
          product_primary_category: selectedProduct.value.c_mHJPrimaryCategory,
        })
        // Constructor integration - Product View event
        trackItemDetailLoad(selectedProduct.value, window.location.href)
      }
    }

    function reset() {
      selectedProduct.value = null
      selectedBasketItemId.value = null
      selectedRingSize.value = undefined
      selectedPcpOption.value = null
      engravingText.value = undefined
      ringSizeError.value = undefined
      masterProduct.value = null
    }

    return {
      // state
      selectedProduct,
      selectedBasketItemId,
      selectedRingSize,
      selectedPcpOption,
      engravingText,
      ringSizeError,
      ringSizeErrorAttempt,
      masterProduct,
      isFetching,
      // getters
      isProductNotFound,
      isEngraved,
      isMutliSizeProductWithoutSelectedSize,
      productAvailabilityModel,
      selectedProcessedSizeOptions,
      selectedProductConstructorId,
      selectedProductUSPs,
      // actions
      setSelectedRingSize,
      applyDefaultRingSize,
      rememberSelectedRingSize,
      setSelectedBasketItem,
      setPcpOption,
      setEngravingText,
      setProduct,
      setRingSizeError,
      getNewVariant,
      reset,
    }
  })()
}
